import {
  AttestationSignerRole,
  CommitteeFilingJurisdiction,
  CommitteeOrganizationType,
  MessagingProvisioningStatus,
} from '../generated/enums.gen';

/**
 * What a campaign is told about its 10DLC registration. The operator's statuses collapse into
 * words a campaign owner understands: both Rejected and Failed need the owner to act.
 */
export const REGISTRATION_LABELS: Record<MessagingProvisioningStatus, string> = {
  [MessagingProvisioningStatus.NotStarted]: 'Not started',
  [MessagingProvisioningStatus.InfoCollected]: 'Submitted',
  [MessagingProvisioningStatus.SubmittedToVendor]: 'In review',
  [MessagingProvisioningStatus.Provisioned]: 'Approved',
  [MessagingProvisioningStatus.Rejected]: 'Needs attention',
  [MessagingProvisioningStatus.Failed]: 'Needs attention',
};

export function registrationLabel(status: MessagingProvisioningStatus): string {
  return REGISTRATION_LABELS[status] ?? status;
}

/**
 * What the platform operator reads on the registration queue: one word per status, so a Rejected
 * attempt (the campaign fixes its facts) and a Failed one (the attempt is over) stay apart.
 */
export const OPERATOR_REGISTRATION_LABELS: Record<MessagingProvisioningStatus, string> = {
  [MessagingProvisioningStatus.NotStarted]: 'Not started',
  [MessagingProvisioningStatus.InfoCollected]: 'Info collected',
  [MessagingProvisioningStatus.SubmittedToVendor]: 'Submitted to vendor',
  [MessagingProvisioningStatus.Provisioned]: 'Provisioned',
  [MessagingProvisioningStatus.Rejected]: 'Rejected',
  [MessagingProvisioningStatus.Failed]: 'Failed',
};

/** Whether the campaign has to do something before its registration can go on. */
export function registrationNeedsAttention(status: MessagingProvisioningStatus): boolean {
  return status === MessagingProvisioningStatus.Rejected || status === MessagingProvisioningStatus.Failed;
}

/** The committee-facts form's choices, worded as the Vendor's registration form words them. */
export const COMMITTEE_ORGANIZATION_TYPE_LABELS: Record<CommitteeOrganizationType, string> = {
  [CommitteeOrganizationType.CandidateCommittee]: 'Candidate committee',
  [CommitteeOrganizationType.PoliticalActionCommittee]: 'Political action committee (PAC)',
  [CommitteeOrganizationType.PartyCommittee]: 'Party committee',
  [CommitteeOrganizationType.NonProfit501c4]: 'Nonprofit 501(c)(4)',
  [CommitteeOrganizationType.BusinessOrSoleProprietor]: 'Business or sole proprietor',
  [CommitteeOrganizationType.Other]: 'Other',
};

export const FILING_JURISDICTION_LABELS: Record<CommitteeFilingJurisdiction, string> = {
  [CommitteeFilingJurisdiction.Federal]: 'Federal (FEC)',
  [CommitteeFilingJurisdiction.State]: 'State',
  [CommitteeFilingJurisdiction.NotSure]: 'Not sure',
};

export const SIGNER_ROLE_LABELS: Record<AttestationSignerRole, string> = {
  [AttestationSignerRole.Candidate]: 'Candidate',
  [AttestationSignerRole.Treasurer]: 'Treasurer',
  [AttestationSignerRole.CampaignManager]: 'Campaign manager',
  [AttestationSignerRole.AuthorizedStaff]: 'Authorized staff',
  [AttestationSignerRole.Other]: 'Other',
};
